import { Link, Stack, Typography } from "@mui/material";
import AcxButton from "components/UI/AcxButton";
import AcxDialog from "components/UI/Dialog/AcxDialog";
import { observer } from "mobx-react";
import React from "react";
import { useStore } from "utils/useStore";
import { LLMClassifierBuilderStore } from "../Stores/LLMClassifierBuilderStore";
import { LLMClassifierBuilderStepperStore } from "../Stores/LLMClassifierBuilderStepperStore";
import {
    isLLMClassifierVersionPublished,
    llmClassifierVersionLabel,
} from "../utils/llmClassifierVersionDisplay";

type UsePublishConfirmationArgs = {
    onConfirmPublish: () => unknown | Promise<unknown>;
};

/**
 * Publish deploys a version over whatever is live, so it asks first -- as unpublish already did. The
 * dialog is also where the two publish behaviours are explained, since that is the only moment the
 * difference between them matters.
 */
export function usePublishConfirmation(args: UsePublishConfirmationArgs) {
    const store = useStore(LLMClassifierBuilderStore);
    const [isOpen, setIsOpen] = React.useState(false);
    const [isLoading, setIsLoading] = React.useState(false);

    const openConfirmation = () => {
        // The dialog describes the version being replaced, which only the version list knows about.
        store.loadLLMClassifierVersions();
        setIsOpen(true);
    };

    const confirm = async () => {
        setIsLoading(true);
        try {
            await args.onConfirmPublish();
        } finally {
            setIsLoading(false);
            setIsOpen(false);
        }
    };

    const confirmationDialog = (
        <PublishConfirmationDialog
            isOpen={isOpen}
            isLoading={isLoading}
            onClose={() => setIsOpen(false)}
            onConfirm={confirm}
        />
    );

    return { openConfirmation, confirmationDialog, isLoading };
}

const PublishConfirmationDialog = observer(function (props: {
    isOpen: boolean;
    isLoading: boolean;
    onClose: () => void;
    onConfirm: () => void;
}) {
    const store = useStore(LLMClassifierBuilderStore);
    const stepper = useStore(LLMClassifierBuilderStepperStore);

    const loadedVersionId = store.classifier.implementation.id;
    const publishedVersion = store.llmClassifierVersions.find((version) =>
        isLLMClassifierVersionPublished(store.classifier.isPublished, version),
    );

    const loadedLabel =
        llmClassifierVersionLabel(store.classifier.implementation.createdOn) ??
        "this version";
    const publishedLabel = llmClassifierVersionLabel(
        publishedVersion?.createdOn,
    );

    const replacesPublished =
        publishedVersion != null && publishedVersion.id !== loadedVersionId;

    // Version history opens on exactly this pair -- the loaded version against the live one -- so the
    // link needs no arguments to land on the diff this publish would apply.
    const openComparison = () => {
        props.onClose();
        stepper.setCurrentWizardStep("approvals", true);
    };

    return (
        <AcxDialog
            isOpen={props.isOpen}
            onClose={props.onClose}
            title="Publish this version?"
            maxWidth="sm"
            buttonsDisabled={props.isLoading}
            dialogContentChildren={
                <Stack rowGap={2}>
                    <Typography color="gray.700">
                        {replacesPublished
                            ? `${loadedLabel} becomes the published version, replacing ${publishedLabel}.`
                            : `${loadedLabel} will become the published version and be used for new processing.`}
                    </Typography>
                    {replacesPublished && (
                        <Link
                            component="button"
                            type="button"
                            variant="body2"
                            underline="hover"
                            onClick={openComparison}
                            sx={{ alignSelf: "flex-start" }}
                        >
                            Compare with the published version
                        </Link>
                    )}
                </Stack>
            }
        >
            <AcxButton loading={props.isLoading} onClick={props.onConfirm}>
                Publish
            </AcxButton>
        </AcxDialog>
    );
});
